import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Sparkles } from 'lucide-react';
import PolicyStatusBadge from './PolicyStatusBadge';
import securityApi from '../../../api/securityApi';
import toast from 'react-hot-toast';

const PolicyRow = ({ policy, projectId, onPolicyUpdated }) => {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async (e) => {
    e.stopPropagation();
    setGenerating(true);
    try {
      await securityApi.generatePolicy(projectId, policy.policy_type);
      // Poll until this policy type appears in completed list
      const poll = setInterval(async () => {
        try {
          const status = await securityApi.getPolicyGenerationStatus(projectId);
          if (
            status.policy_types_completed?.includes(policy.policy_type) ||
            status.policy_types_failed?.includes(policy.policy_type) ||
            status.status === 'completed' ||
            status.status === 'failed'
          ) {
            clearInterval(poll);
            setGenerating(false);
            if (status.policy_types_failed?.includes(policy.policy_type)) {
              toast.error(`Failed to generate "${policy.title}"`);
            } else {
              toast.success(`Policy "${policy.title}" generated`);
            }
            if (onPolicyUpdated) onPolicyUpdated();
          }
        } catch {
          clearInterval(poll);
          setGenerating(false);
          if (onPolicyUpdated) onPolicyUpdated();
        }
      }, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate policy');
      setGenerating(false);
    }
  };

  return (
    <div
      onClick={() => navigate(`/compliance/projects/${projectId}/policies/${policy.id}`)}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors cursor-pointer"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
              {policy.policy_type}
            </span>
            <PolicyStatusBadge status={policy.status} />
          </div>
          <h4 className="text-sm font-medium text-gray-900">{policy.title}</h4>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            <span>Updated {new Date(policy.updated_at).toLocaleDateString()}</span>
            {policy.status === 'approved' && policy.approved_by && (
              <span>Approved by {policy.approved_by}</span>
            )}
            {policy.version && (
              <span className="bg-gray-100 px-1.5 py-0.5 rounded">v{policy.version}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {(!policy.content || policy.status === 'draft') && (
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors disabled:opacity-50"
            >
              {generating ? (
                <Loader className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              Generate
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyRow;
